"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { Idea } from "@/types/thread";
import { useAppSelector, useAppDispatch } from "@/redux/store";
import {
  setRoomProperties,
  setGenerations,
  getComments,
  setViewMode,
} from "@/redux/actions";
import { useSessionStorage } from "@/hooks/useStorage";
import FirstGenerationView from "@/components/idea/FirstGenerationView";
import GenInputs from "@/components/idea/GenInputs";
import MobileLayout from "@/components/idea/MobileLayout";
import Comments from "@/components/social/comments/Comments";
import Thread from "@/components/idea/Thread";

interface ThreadPageContent {
  data: Idea;
  roomUUID: string;
}

const ThreadPageContent: React.FC<ThreadPageContent> = ({ data, roomUUID }) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const commentsQueryParam = searchParams?.get("displayComments");
  const [commentsExpanded, setCommentsExpanded] = useState(
    Boolean(commentsQueryParam)
  );
  const [isFirstGeneration, setIsFirstGeneration] = useSessionStorage(
    "isFirstGeneration",
    true
  );
  const genInputsRef = useRef<HTMLDivElement>(null);

  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  const authLoading = useAppSelector((state) => state.user.authLoading);
  const viewingUserId = useAppSelector((state) => state.user.user?.id);
  const threadProperties = useAppSelector((state) => state.room.roomProperties);
  const existingRoomUserId = threadProperties?.userId || null;
  const generations = useAppSelector((state) => state.room.generations);
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const hasActiveJobs = Boolean(user?.activeJobs && user.activeJobs.length > 0);

  const publicView =
    !isAuthenticated ||
    (Boolean(existingRoomUserId) && viewingUserId !== existingRoomUserId);

  const toggleComments = () => {
    setCommentsExpanded(!commentsExpanded);
  };

  const handleGenerationStart = () => {
    setTimeout(() => {
      if (genInputsRef.current) {
        genInputsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (hasActiveJobs && !publicView && genInputsRef.current) {
      setTimeout(() => {
        genInputsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500);
    }
  }, [hasActiveJobs]);

  useEffect(() => {
    let interval: Nullable<ReturnType<typeof setInterval>> = null;
    if (!authLoading) {
      dispatch(getComments({ roomUUID }));

      // Replace with websocket
      const dispatchFunction = () => {
        dispatch(getComments({ roomUUID }));
      };

      interval = setInterval(dispatchFunction, 60000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [roomUUID, authLoading]);

  useEffect(() => {
    if (!publicView && generations.length > 0) {
      const latestGeneration = generations[generations.length - 1];
      const selectedViewMode = latestGeneration.viewMode;
      dispatch(setViewMode(selectedViewMode));
    }
  }, [generations, publicView]);

  useEffect(() => {
    const { generations, ...rest } = data;
    dispatch(setRoomProperties(rest));
    dispatch(setGenerations(data.generations || []));
  }, [data]);

  if (
    isFirstGeneration &&
    generations.length === 1 &&
    !publicView &&
    isMobile &&
    !isSubscribed
  ) {
    return (
      <FirstGenerationView
        generation={generations[0]}
        onShowThread={() => setIsFirstGeneration(false)}
      />
    );
  }

  return (
    <div className={classes.container}>
      <Thread
        publicView={publicView}
        title={data.seoTitle}
        onToggleComments={toggleComments}
      />
      {commentsExpanded && (
        <div className={classes.collapseCommentsButton}>
          <IconButton
            className={classes.commentsButton}
            onClick={toggleComments}
          >
            <ExpandLessIcon />
          </IconButton>
        </div>
      )}
      <div className={classes.commentsContainer}>
        <Comments
          currentUserId={viewingUserId ? viewingUserId : null}
          roomUUID={roomUUID}
          className={
            commentsExpanded
              ? classes.expandedContainer
              : classes.collapsedContainer
          }
        />
      </div>
      {!commentsExpanded && !publicView && (
        <div ref={genInputsRef}>
          {isMobile ? (
            <MobileLayout
              existingRoomUUID={roomUUID}
              showHeading={false}
              initialConfig={{
                selectedModes: {
                  isPublic:
                    generations[generations.length - 1]?.viewMode === "public",
                  is18Plus: false,
                  isTextImage: false,
                },
                aspectRatio:
                  generations[generations.length - 1]?.aspectRatioUsed || "1:1",
                selectedModel:
                  generations[generations.length - 1]?.modelUsed || "auto",
                totalImages:
                  generations[generations.length - 1]?.images.length || 4,
              }}
            />
          ) : (
            <GenInputs
              toggleComments={toggleComments}
              roomUUID={roomUUID}
              onGenerationStart={handleGenerationStart}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ThreadPageContent;
